import React, { useState, useEffect, useCallback } from 'react';
import { Player } from '../../types';
import './PlayerInfoCard.css';

// Interface for API response data
interface ApiPlayerData {
  idPlayer: string;
  strPlayer: string;
  strThumb: string | null;
  strCutout: string | null;
  strTeam: string;
  strPosition: string;
  strDescriptionEN: string | null;
  strHeight: string | null;
  strWeight: string | null;
  strNationality: string | null;
  dateBorn: string | null;
  strBirthLocation: string | null;
}

// Cache to store fetched player data
const playerDataCache: Record<string, ApiPlayerData> = {};

interface PlayerInfoCardProps {
  player: Player;
  onClose: () => void;
  position: string;
}

const PlayerInfoCard: React.FC<PlayerInfoCardProps> = ({ player, onClose, position }) => {
  const [apiPlayerData, setApiPlayerData] = useState<ApiPlayerData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Function to search for player by name
  const searchPlayerByName = useCallback(async (playerName: string) => {
    const searchUrl = `https://www.thesportsdb.com/api/v1/json/3/searchplayers.php?p=${encodeURIComponent(playerName)}`;
    
    try {
      const response = await fetch(searchUrl);
      if (!response.ok) {
        throw new Error('Failed to search for player');
      }
      
      const data = await response.json();
      if (data.player && data.player.length > 0) {
        // Use the first result's ID to fetch detailed data
        return data.player[0].idPlayer;
      }
      return null;
    } catch (error) {
      console.error('Error searching for player:', error);
      return null;
    }
  }, []);

  // Function to fetch player data from API
  const fetchPlayerData = useCallback(async (playerId: string) => {
    // If data is in cache, use it
    if (playerDataCache[playerId]) {
      setApiPlayerData(playerDataCache[playerId]);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`https://www.thesportsdb.com/api/v1/json/3/lookupplayer.php?id=${playerId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch player data');
      }
      
      const data = await response.json();
      if (data.players && data.players.length > 0) {
        const playerData = data.players[0];
        // Store in cache
        playerDataCache[playerId] = playerData;
        setApiPlayerData(playerData);
      } else {
        throw new Error('No player data found');
      }
    } catch (err) {
      setError((err as Error).message);
      console.error('Error fetching player data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Try to fetch player data when component mounts
  useEffect(() => {
    const getPlayerData = async () => {
      // First try with existing apiId if available
      if (player.apiId) {
        console.log('Using existing apiId:', player.apiId);
        fetchPlayerData(player.apiId);
      } else {
        // Try to search by name if no apiId is available
        console.log('Searching for player by name:', player.name);
        const playerId = await searchPlayerByName(player.name);
        if (playerId) {
          console.log('Found player ID:', playerId);
          fetchPlayerData(playerId);
        } else {
          console.log('No player found with name:', player.name);
          setError('Player not found in database');
          setIsLoading(false);
        }
      }
    };

    getPlayerData();
  }, [player, fetchPlayerData, searchPlayerByName]);

  if (!player) return null;

  // Function to format positions nicely
  const formatPosition = (pos: string): string => {
    // Map of position abbreviations to full names
    const positionMap: {[key: string]: string} = {
      'GK': 'Goalkeeper',
      'LB': 'Left Back',
      'RB': 'Right Back',
      'CB': 'Center Back',
      'LCB': 'Left Center Back',
      'RCB': 'Right Center Back',
      'CCB': 'Center Back',
      'CDM': 'Defensive Mid',
      'CM': 'Center Mid',
      'CAM': 'Attacking Mid',
      'LM': 'Left Mid',
      'RM': 'Right Mid',
      'LW': 'Left Wing',
      'RW': 'Right Wing',
      'ST': 'Striker',
      'CF': 'Center Forward',
      'LWB': 'Left Wing Back',
      'RWB': 'Right Wing Back',
      'LST': 'Left Striker',
      'RST': 'Right Striker',
      'LCM': 'Left Center Mid',
      'RCM': 'Right Center Mid',
      'LCDM': 'Left Defensive Mid',
      'RCDM': 'Right Defensive Mid'
    };
    
    return positionMap[pos] || pos;
  };

  // Get primary position (from the field)
  const primaryPosition = formatPosition(position);
  
  // Get player's natural position if different from field position
  const naturalPosition = player.position !== position ? formatPosition(player.position) : null;

  // Define icons for stats
  const getStatIcon = (statType: string): string => {
    switch (statType) {
      case 'team':
        return '👕';
      case 'country':
        return '🌍';
      case 'year':
        return '📅';
      case 'goals':
        return '⚽';
      case 'assists':
        return '👟';
      case 'height':
        return '📏';
      case 'weight':
        return '⚖️';
      case 'birthplace':
        return '🏠';
      case 'birthday':
        return '🎂';
      default:
        return '📊';
    }
  };

  // Format date from API (YYYY-MM-DD to more readable format)
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Get player image with API image as fallback
  const getPlayerImage = (): string => {
    // First try local image
    if (player.imageUrl) return player.imageUrl;
    
    // Then try API images
    if (apiPlayerData?.strCutout) return apiPlayerData.strCutout;
    if (apiPlayerData?.strThumb) return apiPlayerData.strThumb;
    
    // Default placeholder
    return 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=?';
  };

  return (
    <div className="player-info-card-overlay" onClick={onClose}>
      <div className="player-info-card" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="player-info-header">
          <div className="player-image-container">
            <img 
              src={getPlayerImage()} 
              alt={player.name} 
              className="player-card-image" 
              onError={(e) => {
                const canvas = document.createElement('canvas');
                canvas.width = 90;
                canvas.height = 90;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  // Create better silhouette background
                  ctx.fillStyle = '#4CAF50';
                  ctx.beginPath();
                  ctx.arc(45, 45, 45, 0, 2 * Math.PI);
                  ctx.fill();
                  
                  // Draw player silhouette
                  ctx.fillStyle = 'rgba(255,255,255,0.7)';
                  // Head
                  ctx.beginPath();
                  ctx.arc(45, 27, 14, 0, 2 * Math.PI);
                  ctx.fill();
                  
                  // Body
                  ctx.beginPath();
                  ctx.moveTo(30, 45);
                  ctx.lineTo(60, 45);
                  ctx.lineTo(65, 80);
                  ctx.lineTo(25, 80);
                  ctx.closePath();
                  ctx.fill();
                  
                  // Set as image source
                  (e.target as HTMLImageElement).src = canvas.toDataURL();
                }
              }}
            />
            {isLoading && <div className="loading-indicator">Loading...</div>}
          </div>
          <div className="player-info-details">
            <h3 className="player-name">{player.name}</h3>
            <div className="player-positions">
              <span className="position-badge">{primaryPosition}</span>
              {naturalPosition && (
                <span className="position-badge">{naturalPosition}</span>
              )}
              {apiPlayerData?.strPosition && apiPlayerData.strPosition !== primaryPosition && (
                <span className="position-badge api-position">{apiPlayerData.strPosition}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="player-stats">
          {(apiPlayerData?.strTeam || player.team) && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('team')}</span>
                <span className="stat-label">Team</span>
              </div>
              <span className="stat-value">{apiPlayerData?.strTeam || player.team}</span>
            </div>
          )}
          
          {(apiPlayerData?.strNationality || player.country) && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('country')}</span>
                <span className="stat-label">Country</span>
              </div>
              <span className="stat-value">{apiPlayerData?.strNationality || player.country}</span>
            </div>
          )}
          
          {apiPlayerData?.dateBorn && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('birthday')}</span>
                <span className="stat-label">Born</span>
              </div>
              <span className="stat-value">{formatDate(apiPlayerData.dateBorn)}</span>
            </div>
          )}
          
          {apiPlayerData?.strBirthLocation && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('birthplace')}</span>
                <span className="stat-label">Birthplace</span>
              </div>
              <span className="stat-value">{apiPlayerData.strBirthLocation}</span>
            </div>
          )}
          
          {apiPlayerData?.strHeight && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('height')}</span>
                <span className="stat-label">Height</span>
              </div>
              <span className="stat-value">{apiPlayerData.strHeight}</span>
            </div>
          )}
          
          {apiPlayerData?.strWeight && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('weight')}</span>
                <span className="stat-label">Weight</span>
              </div>
              <span className="stat-value">{apiPlayerData.strWeight}</span>
            </div>
          )}
          
          {(player.year || apiPlayerData?.dateBorn) && !apiPlayerData?.strBirthLocation && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('year')}</span>
                <span className="stat-label">Year</span>
              </div>
              <span className="stat-value">{player.year || (apiPlayerData?.dateBorn ? new Date(apiPlayerData.dateBorn).getFullYear() : '')}</span>
            </div>
          )}
          
          {player.goals && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('goals')}</span>
                <span className="stat-label">Goals</span>
              </div>
              <span className="stat-value">{player.goals}</span>
            </div>
          )}
          
          {player.assists && (
            <div className="stat-item">
              <div className="stat-label-with-icon">
                <span className="stat-icon">{getStatIcon('assists')}</span>
                <span className="stat-label">Assists</span>
              </div>
              <span className="stat-value">{player.assists}</span>
            </div>
          )}

          {apiPlayerData?.strDescriptionEN && (
            <div>
              <h4>Biography</h4>
            <div className="player-bio">
              
              <p className="bio-text">{apiPlayerData.strDescriptionEN.split('\\r\\n').join(' ')}</p>
            </div>
            </div>
          )}
          
          {error && <div className="api-error">Failed to load additional data</div>}
        </div>
      </div>
    </div>
  );
};

export default PlayerInfoCard; 